"use client";

import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { VariantProps, cva } from "class-variance-authority";
import { SidebarLeftIcon } from "./SidebarLeftIcon";

import { useIsMobile } from "./use-mobile";
import { cn } from "./utils";
import { Button } from "./button";
import { Input } from "./input";
import { Separator } from "./separator";
import { createPortal } from "react-dom";
import { Skeleton } from "./skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./tooltip";
import { prefersReducedMotion } from "../../utils/reducedMotion";

const SIDEBAR_COOKIE_NAME = "sidebar_state";
const SIDEBAR_COOKIE_MAX_AGE = 60 * 60 * 24 * 7;
const SIDEBAR_WIDTH = "16rem";
const SIDEBAR_WIDTH_MOBILE = "18rem";
const SIDEBAR_WIDTH_ICON = "3rem";
const SIDEBAR_KEYBOARD_SHORTCUT = "b";
const MOBILE_CARD_RADIUS = 28;

const hasRadixSlot = typeof Slot !== "undefined";
let hasWarnedMissingSlot = false;
const warnMissingSlot = () => {
  if (hasWarnedMissingSlot) return;
  hasWarnedMissingSlot = true;
  console.warn("[ui/sidebar] Radix Slot unavailable. Falling back to native elements.");
};

type SidebarContextProps = {
  state: "expanded" | "collapsed";
  open: boolean;
  setOpen: (open: boolean) => void;
  openMobile: boolean;
  setOpenMobile: (open: boolean) => void;
  isMobile: boolean;
  toggleSidebar: () => void;
};

const SidebarContext = React.createContext<SidebarContextProps | null>(null);

export function useSidebar() {
  const context = React.useContext(SidebarContext);
  if (!context) {
    throw new Error("useSidebar must be used within a SidebarProvider.");
  }

  return context;
}

export function SidebarProvider({
  defaultOpen = true,
  open: openProp,
  onOpenChange: setOpenProp,
  className,
  style,
  children,
  ...props
}: React.ComponentProps<"div"> & {
  defaultOpen?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}) {
  const isMobile = useIsMobile();
  const [openMobile, setOpenMobile] = React.useState(false);

  const [_open, _setOpen] = React.useState(defaultOpen);
  const open = openProp ?? _open;
  const setOpen = React.useCallback(
    (value: boolean | ((value: boolean) => boolean)) => {
      const openState = typeof value === "function" ? value(open) : value;
      if (setOpenProp) {
        setOpenProp(openState);
      } else {
        _setOpen(openState);
      }

      document.cookie = `${SIDEBAR_COOKIE_NAME}=${openState}; path=/; max-age=${SIDEBAR_COOKIE_MAX_AGE}`;
    },
    [setOpenProp, open],
  );

  const toggleSidebar = React.useCallback(() => {
    return isMobile ? setOpenMobile((open) => !open) : setOpen((open) => !open);
  }, [isMobile, setOpen, setOpenMobile]);

  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (
        event.key === SIDEBAR_KEYBOARD_SHORTCUT &&
        (event.metaKey || event.ctrlKey)
      ) {
        event.preventDefault();
        toggleSidebar();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [toggleSidebar]);

  const state = open ? "expanded" : "collapsed";

  const contextValue = React.useMemo<SidebarContextProps>(
    () => ({
      state,
      open,
      setOpen,
      isMobile,
      openMobile,
      setOpenMobile,
      toggleSidebar,
    }),
    [state, open, setOpen, isMobile, openMobile, setOpenMobile, toggleSidebar],
  );

  const { _fgT, _fgt, _fgS, _fgs, _fgB, _fgb, ...cleanProps } = props as any;

  return (
    <SidebarContext.Provider value={contextValue}>
      <TooltipProvider delayDuration={0}>
        <div
          data-slot="sidebar-wrapper"
          style={
            {
              "--sidebar-width": SIDEBAR_WIDTH,
              "--sidebar-width-icon": SIDEBAR_WIDTH_ICON,
              ...style,
            } as React.CSSProperties
          }
          className={cn(
            "group/sidebar-wrapper has-data-[variant=inset]:bg-sidebar flex min-h-svh w-full",
            className,
          )}
          {...cleanProps}
        >
          {children}
        </div>
      </TooltipProvider>
    </SidebarContext.Provider>
  );
}

export function Sidebar({
  side = "left",
  variant = "sidebar",
  collapsible = "offcanvas",
  className,
  children,
  ...props
}: React.ComponentProps<"div"> & {
  side?: "left" | "right";
  variant?: "sidebar" | "floating" | "inset";
  collapsible?: "offcanvas" | "icon" | "none";
}) {
  const { isMobile, state, openMobile, setOpenMobile } = useSidebar();
  const sectionNavigationOnly =
    (props as Record<string, unknown>)["data-section-navigation-only"] === true ||
    (props as Record<string, unknown>)["data-project-navigation-only"] === true;
  const [shouldRender, setShouldRender] = React.useState(false);
  const [panelActive, setPanelActive] = React.useState(false);
  const touchStartXRef = React.useRef<number | null>(null);
  const touchStartYRef = React.useRef<number | null>(null);
  const swipeActiveRef = React.useRef(false);
  const dragDistanceRef = React.useRef(0);
  const drawerWidthRef = React.useRef(288);
  const cardPositionRef = React.useRef(0);
  const gestureStartPositionRef = React.useRef(0);
  const pendingDragPositionRef = React.useRef(0);
  const dragPaintFrameRef = React.useRef<number | null>(null);
  const suppressNextClickRef = React.useRef(false);
  const openFrameRef = React.useRef<number | null>(null);
  const settleFrameRef = React.useRef<number | null>(null);

  React.useEffect(() => {
    if (openMobile) {
      if (!shouldRender) {
        setShouldRender(true);
        return;
      }
      if (panelActive || openFrameRef.current !== null) return;

      const drawer = document.querySelector<HTMLElement>('[data-slot="sidebar"][data-mobile="true"]');
      drawerWidthRef.current = drawer?.getBoundingClientRect().width ?? 288;
      cardPositionRef.current = 0;
      dragDistanceRef.current = 0;
      setPanelActive(true);
      openFrameRef.current = requestAnimationFrame(() => {
        openFrameRef.current = null;
        const root = document.documentElement;
        root.setAttribute("data-sidebar-dragging", "true");
        root.style.setProperty("--qs-sidebar-push-x", "0px");
        root.style.setProperty("--qs-sidebar-card-radius", "0px");
        settleMobileCard(drawerWidthRef.current, () => {
          root.style.removeProperty("--qs-sidebar-push-x");
          root.style.removeProperty("--qs-sidebar-card-radius");
        });
      });
      return;
    }
    if (openFrameRef.current !== null) cancelAnimationFrame(openFrameRef.current);
    openFrameRef.current = null;
    if (!shouldRender) return;
    if (panelActive) {
      document.documentElement.setAttribute("data-sidebar-dragging", "true");
      settleMobileCard(0, () => {
        // The menu is a full-viewport portal beneath the pushed app card. Tear
        // both states down in the same commit; leaving the portal mounted after
        // the app loses its z-layer lets the translucent menu flash through the
        // closed page for one or more frames.
        setShouldRender(false);
        setPanelActive(false);
      });
      return;
    }
    setShouldRender(false);
  }, [openMobile, panelActive, shouldRender]);

  // The push state lives on <html>, outside this component's subtree. Its CSS
  // variables drive AppLayout's inner card layer, leaving the outer app root's
  // Visual Viewport keyboard transform independent.
  React.useEffect(() => {
    if (!isMobile) return;
    const root = document.documentElement;
    if (panelActive) root.setAttribute("data-sidebar-pushed", "true");
    else {
      root.removeAttribute("data-sidebar-pushed");
      root.removeAttribute("data-sidebar-dragging");
      root.style.removeProperty("--qs-sidebar-push-x");
      root.style.removeProperty("--qs-sidebar-card-radius");
      cardPositionRef.current = 0;
    }
    return () => root.removeAttribute("data-sidebar-pushed");
  }, [isMobile, panelActive]);

  // The gesture writes directly to the compositor-facing transform variable.
  // React state is intentionally absent from this hot path: touchmove can fire
  // at 120 Hz, and a render between release and the CSS transition used to
  // discard the final drag frame, making the card visibly snap backwards.
  React.useEffect(() => {
    return () => {
      if (settleFrameRef.current !== null) cancelAnimationFrame(settleFrameRef.current);
      settleFrameRef.current = null;
      if (dragPaintFrameRef.current !== null) cancelAnimationFrame(dragPaintFrameRef.current);
      dragPaintFrameRef.current = null;
      if (openFrameRef.current !== null) cancelAnimationFrame(openFrameRef.current);
      openFrameRef.current = null;
      const root = document.documentElement;
      root.removeAttribute("data-sidebar-dragging");
      root.style.removeProperty("--qs-sidebar-push-x");
      root.style.removeProperty("--qs-sidebar-card-radius");
    };
  }, []);

  function paintMobileCardPosition(x: number) {
    const clampedX = Math.max(0, Math.min(drawerWidthRef.current, Math.round(x)));
    const radius = Math.round(MOBILE_CARD_RADIUS * (clampedX / drawerWidthRef.current));
    cardPositionRef.current = clampedX;
    const root = document.documentElement;
    root.style.setProperty("--qs-sidebar-push-x", `${clampedX}px`);
    root.style.setProperty("--qs-sidebar-card-radius", `${radius}px`);
  }

  function settleMobileCard(targetX: number, onSettled: () => void) {
    const root = document.documentElement;
    const currentX = Math.round(cardPositionRef.current);
    const roundedTargetX = Math.round(targetX);

    paintMobileCardPosition(currentX);
    if (settleFrameRef.current !== null) cancelAnimationFrame(settleFrameRef.current);

    const complete = () => {
      paintMobileCardPosition(roundedTargetX);
      settleFrameRef.current = null;
      root.removeAttribute("data-sidebar-dragging");
      dragDistanceRef.current = 0;
      onSettled();
    };

    if (prefersReducedMotion()) {
      complete();
      return;
    }

    const distance = Math.abs(roundedTargetX - currentX);
    const duration = Math.min(260, 160 + distance * 0.3);
    const startedAt = performance.now();
    const tick = (now: number) => {
      const progress = Math.min(1, (now - startedAt) / duration);
      const eased = 1 - Math.pow(1 - progress, 4);
      const nextX = Math.round(currentX + (roundedTargetX - currentX) * eased);
      paintMobileCardPosition(nextX);
      if (progress < 1) {
        settleFrameRef.current = requestAnimationFrame(tick);
      } else {
        complete();
      }
    };
    settleFrameRef.current = requestAnimationFrame(tick);
  }

  const handleTouchStart = (event: React.TouchEvent) => {
    if (!openMobile) return;
    if (settleFrameRef.current !== null) cancelAnimationFrame(settleFrameRef.current);
    settleFrameRef.current = null;
    touchStartXRef.current = event.touches[0]?.clientX ?? null;
    touchStartYRef.current = event.touches[0]?.clientY ?? null;
    swipeActiveRef.current = false;
    dragDistanceRef.current = 0;
    const drawer = document.querySelector<HTMLElement>('[data-slot="sidebar"][data-mobile="true"]');
    drawerWidthRef.current = drawer?.getBoundingClientRect().width ?? 288;
    gestureStartPositionRef.current = cardPositionRef.current || drawerWidthRef.current;
    const root = document.documentElement;
    root.setAttribute("data-sidebar-dragging", "true");
    root.style.setProperty("--qs-sidebar-push-x", `${gestureStartPositionRef.current}px`);
    root.style.setProperty(
      "--qs-sidebar-card-radius",
      `${Math.round(MOBILE_CARD_RADIUS * (gestureStartPositionRef.current / drawerWidthRef.current))}px`,
    );
  };

  const handleTouchMove = (event: React.TouchEvent) => {
    if (touchStartXRef.current === null || touchStartYRef.current === null) return;
    const currentX = event.touches[0]?.clientX ?? touchStartXRef.current;
    const currentY = event.touches[0]?.clientY ?? touchStartYRef.current;
    const dx = currentX - touchStartXRef.current;
    const dy = currentY - touchStartYRef.current;

    if (!swipeActiveRef.current) {
      if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
      if (Math.abs(dy) > Math.abs(dx)) {
        return;
      }
      swipeActiveRef.current = true;
    }

    // What moves is the app card, not the menu, so the closing gesture runs the
    // opposite way to the reveal: a left-hand menu is closed by dragging the
    // card back LEFT over it.
    const delta = side === "left"
      ? Math.max(0, -dx)
      : Math.max(0, dx);
    const clampedDelta = Math.round(Math.min(drawerWidthRef.current, delta));
    dragDistanceRef.current = clampedDelta;
    const pushX = Math.max(0, gestureStartPositionRef.current - clampedDelta);
    cardPositionRef.current = pushX;
    pendingDragPositionRef.current = pushX;
    if (dragPaintFrameRef.current === null) {
      dragPaintFrameRef.current = requestAnimationFrame(() => {
        dragPaintFrameRef.current = null;
        paintMobileCardPosition(pendingDragPositionRef.current);
      });
    }
  };

  const handleTouchEnd = () => {
    if (dragPaintFrameRef.current !== null) cancelAnimationFrame(dragPaintFrameRef.current);
    dragPaintFrameRef.current = null;
    paintMobileCardPosition(cardPositionRef.current);
    if (!swipeActiveRef.current) {
      touchStartXRef.current = null;
      touchStartYRef.current = null;
      document.documentElement.removeAttribute("data-sidebar-dragging");
      document.documentElement.style.removeProperty("--qs-sidebar-push-x");
      document.documentElement.style.removeProperty("--qs-sidebar-card-radius");
      return;
    }
    const closeThreshold = Math.min(90, drawerWidthRef.current * 0.32);
    const shouldClose = dragDistanceRef.current > closeThreshold;
    touchStartXRef.current = null;
    touchStartYRef.current = null;
    swipeActiveRef.current = false;
    suppressNextClickRef.current = true;
    window.setTimeout(() => { suppressNextClickRef.current = false; }, 0);
    if (shouldClose) {
      setOpenMobile(false);
    } else {
      settleMobileCard(drawerWidthRef.current, () => {
        document.documentElement.style.removeProperty("--qs-sidebar-push-x");
        document.documentElement.style.removeProperty("--qs-sidebar-card-radius");
      });
    }
  };

  const handleTouchCancel = () => {
    if (dragPaintFrameRef.current !== null) cancelAnimationFrame(dragPaintFrameRef.current);
    dragPaintFrameRef.current = null;
    touchStartXRef.current = null;
    touchStartYRef.current = null;
    swipeActiveRef.current = false;
    suppressNextClickRef.current = true;
    window.setTimeout(() => { suppressNextClickRef.current = false; }, 0);
    settleMobileCard(drawerWidthRef.current, () => {
      document.documentElement.style.removeProperty("--qs-sidebar-push-x");
      document.documentElement.style.removeProperty("--qs-sidebar-card-radius");
    });
  };

  const { _fgT, _fgt, _fgS, _fgs, _fgB, _fgb, ...cleanProps } = props as any;

  // Keep the menu and moving card inside one stacking context. Portalling to
  // body put the menu outside app-root's Visual Viewport transform context,
  // where no z-index on the inner card could reliably order the two layers.
  const mobilePortalHost = typeof document !== "undefined"
    ? document.querySelector<HTMLElement>('[data-slot="app-root"]') ?? document.body
    : null;

  const mobileSidebar = isMobile && shouldRender && mobilePortalHost
    ? createPortal(
        <div
          data-slot="sidebar-mobile-overlay"
          // The BASE layer, deliberately beneath the app shell (which the
          // data-sidebar-pushed rule raises to z-10 and slides aside). A panel
          // laid OVER the app with a scrim is the other pattern entirely — here
          // nothing covers the menu, the app uncovers it.
          // Painted, not transparent: the card is inset and rounded, so the
          // strip above, below and behind it would otherwise show bare page
          // background rather than the menu's own surface continuing behind.
          className={cn(
            "fixed inset-0 z-0",
          )}
          style={
            {
              background: sectionNavigationOnly
                ? "var(--project-navigation-surface)"
                : "var(--background)",
              ...(sectionNavigationOnly
                ? {
                    "--nav-item-text": "var(--project-navigation-text)",
                    "--nav-item-text-hover": "var(--project-navigation-text-hover)",
                    "--nav-item-hover-bg": "var(--project-navigation-hover-bg)",
                    "--nav-item-active-bg": "var(--project-navigation-active-bg)",
                    "--nav-item-active-text": "var(--project-navigation-active-text)",
                  }
                : {}),
            } as React.CSSProperties
          }
          aria-modal="true"
          role="dialog"
        >
          <div
            data-sidebar="sidebar"
            data-slot="sidebar"
            data-mobile="true"
            // No blur, no translucency, no slide of its own: it is the backdrop,
            // so it is fully opaque and simply sits there while the card moves
            // off it. Anything semi-transparent here shows the card sliding
            // around behind the menu, which reads as two competing layers.
            className={cn(
              "absolute inset-y-0 w-(--sidebar-width) bg-[inherit] text-foreground p-0 overflow-y-auto overflow-x-hidden",
              side === "left" ? "left-0" : "right-0",
            )}
            style={
              {
                "--sidebar-width": SIDEBAR_WIDTH_MOBILE,
                WebkitOverflowScrolling: "touch",
                // In the native shells the nav bar and tab bar are native layers
                // ABOVE the webview, so no web z-index can clear them — the
                // drawer's first rows render underneath the bar and are simply
                // unreachable. Pad by the offsets the shell injects onto <html>.
                // Outside a shell both vars are unset and env() resolves to the
                // browser's own safe area, so this is a no-op on desktop.
                paddingTop:
                  "var(--quantis-native-content-top-offset, env(safe-area-inset-top))",
                paddingBottom:
                  "var(--quantis-native-content-bottom-offset, env(safe-area-inset-bottom))",
              } as React.CSSProperties
            }
            onClick={(event) => event.stopPropagation()}
          >
            {children}
          </div>
        </div>,
        mobilePortalHost,
      )
    : null;

  // Tap or swipe the exposed edge of the app card to close. It has to live
  // ABOVE the card (which the push rule puts at z-10) while stopping short of
  // the menu's width, or it would swallow every tap meant for the menu.
  const mobileCardCatcher = isMobile && shouldRender && mobilePortalHost
    ? createPortal(
        <button
          type="button"
          aria-label="Close navigation"
          data-slot="sidebar-mobile-card-catcher"
          // Transparent: purely a hit target. The dim lives on the card itself
          // (an ::after inside app-root) so it inherits the card's rounded
          // corners and scale — painted here it would be a full-height
          // rectangle covering the very inset and corners that make the card
          // read as a card.
          className="fixed inset-y-0 right-0 z-20"
          style={{ left: side === "left" ? SIDEBAR_WIDTH_MOBILE : 0, right: side === "left" ? 0 : SIDEBAR_WIDTH_MOBILE }}
          onClick={() => {
            if (suppressNextClickRef.current) return;
            setOpenMobile(false);
          }}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onTouchCancel={handleTouchCancel}
        />,
        mobilePortalHost,
      )
    : null;

  if (collapsible === "none") {
    return (
      <div
        data-slot="sidebar"
        className={cn(
          "bg-sidebar text-sidebar-foreground flex h-full w-(--sidebar-width) flex-col",
          className,
        )}
        {...cleanProps}
      >
        {children}
      </div>
    );
  }

  if (isMobile) {
    return (
      <>
        {mobileSidebar}
        {mobileCardCatcher}
      </>
    );
  }

  return (
    <div
      className="group peer text-sidebar-foreground hidden lg:block"
      data-state={state}
      data-collapsible={state === "collapsed" ? collapsible : ""}
      data-variant={variant}
      data-side={side}
      data-slot="sidebar"
      {...cleanProps}
    >
      <div
        data-slot="sidebar-gap"
        className={cn(
          "relative w-(--sidebar-width) bg-transparent transition-[width] [transition-duration:var(--duration-slide)] [transition-timing-function:var(--ease-spring)]",
          "group-data-[collapsible=offcanvas]:w-0",
          "group-data-[side=right]:rotate-180",
          variant === "floating" || variant === "inset"
            ? "group-data-[collapsible=icon]:w-[calc(var(--sidebar-width-icon)+(--spacing(4)))]"
            : "group-data-[collapsible=icon]:w-(--sidebar-width-icon)",
        )}
      />
      <div
        data-slot="sidebar-container"
        className={cn(
          "fixed inset-y-0 z-[90] hidden h-svh w-(--sidebar-width) pointer-events-auto transition-[left,right,width] [transition-duration:var(--duration-slide)] [transition-timing-function:var(--ease-spring)] lg:flex",
          side === "left"
            ? "left-0 group-data-[collapsible=offcanvas]:left-[calc(var(--sidebar-width)*-1)]"
            : "right-0 group-data-[collapsible=offcanvas]:right-[calc(var(--sidebar-width)*-1)]",
          variant === "floating" || variant === "inset"
            ? "p-2 group-data-[collapsible=icon]:w-[calc(var(--sidebar-width-icon)+(--spacing(4))+2px)]"
            : "group-data-[collapsible=icon]:w-(--sidebar-width-icon)",
          className,
        )}
      >
        <div
          data-sidebar="sidebar"
          data-slot="sidebar-inner"
          className="group-data-[variant=floating]:border-sidebar-border flex h-full min-h-0 w-full flex-col overflow-hidden group-data-[variant=floating]:rounded-lg group-data-[variant=floating]:border group-data-[variant=floating]:glass-shadow"
        >
          {children}
        </div>
      </div>
    </div>
  );
}

export function SidebarTrigger({
  className,
  onClick,
  ...props
}: React.ComponentProps<typeof Button>) {
  const { toggleSidebar } = useSidebar();
  const { _fgT, _fgt, _fgS, _fgs, _fgB, _fgb, ...cleanProps } = props as any;

  return (
    <Button
      data-sidebar="trigger"
      data-slot="sidebar-trigger"
      variant="ghost"
      size="icon"
      className={cn("size-7", className)}
      onClick={(event) => {
        onClick?.(event);
        toggleSidebar();
      }}
      {...cleanProps}
    >
      <SidebarLeftIcon style={{ height: 16 }} />
      <span className="sr-only">Toggle Sidebar</span>
    </Button>
  );
}

export function SidebarRail({ className, ...props }: React.ComponentProps<"button">) {
  const { toggleSidebar } = useSidebar();
  const { _fgT, _fgt, _fgS, _fgs, _fgB, _fgb, ...cleanProps } = props as any;

  return (
    <button
      data-sidebar="rail"
      data-slot="sidebar-rail"
      aria-label="Toggle Sidebar"
      onClick={toggleSidebar}
      title="Toggle Sidebar"
      className={cn(
        "hover:after:bg-sidebar-border absolute inset-y-0 z-20 hidden w-4 -translate-x-1/2 transition-all ease-linear group-data-[side=left]:-right-4 group-data-[side=right]:left-0 after:absolute after:inset-y-0 after:left-1/2 after:w-[2px] sm:flex",
        "in-data-[side=left]:cursor-w-resize in-data-[side=right]:cursor-e-resize",
        "[[data-side=left][data-state=collapsed]_&]:cursor-e-resize [[data-side=right][data-state=collapsed]_&]:cursor-w-resize",
        "hover:group-data-[collapsible=offcanvas]:bg-sidebar group-data-[collapsible=offcanvas]:translate-x-0 group-data-[collapsible=offcanvas]:after:left-full",
        "[[data-side=left][data-collapsible=offcanvas]_&]:-right-2",
        "[[data-side=right][data-collapsible=offcanvas]_&]:-left-2",
        className,
      )}
      {...cleanProps}
    />
  );
}

export function SidebarInset({ className, ...props }: React.ComponentProps<"main">) {
  const { _fgT, _fgt, _fgS, _fgs, _fgB, _fgb, ...cleanProps } = props as any;
  return (
    <main
      data-slot="sidebar-inset"
      className={cn(
        "relative flex w-full flex-1 flex-col",
        "md:peer-data-[variant=inset]:m-2 md:peer-data-[variant=inset]:ml-0 md:peer-data-[variant=inset]:rounded-xl md:peer-data-[variant=inset]:glass-shadow md:peer-data-[variant=inset]:peer-data-[state=collapsed]:ml-2",
        className,
      )}
      {...cleanProps}
    />
  );
}

export function SidebarInput({
  className,
  ...props
}: React.ComponentProps<typeof Input>) {
  const { _fgT, _fgt, _fgS, _fgs, _fgB, _fgb, ...cleanProps } = props as any;
  return (
    <Input
      data-slot="sidebar-input"
      data-sidebar="input"
      className={cn("bg-background h-8 w-full shadow-none", className)}
      {...cleanProps}
    />
  );
}

export function SidebarHeader({ className, ...props }: React.ComponentProps<"div">) {
  const { _fgT, _fgt, _fgS, _fgs, _fgB, _fgb, ...cleanProps } = props as any;
  return (
    <div
      data-slot="sidebar-header"
      data-sidebar="header"
      className={cn("flex flex-col gap-2 p-2", className)}
      {...cleanProps}
    />
  );
}

export function SidebarFooter({ className, ...props }: React.ComponentProps<"div">) {
  const { _fgT, _fgt, _fgS, _fgs, _fgB, _fgb, ...cleanProps } = props as any;
  return (
    <div
      data-slot="sidebar-footer"
      data-sidebar="footer"
      className={cn("flex flex-col gap-2 p-2", className)}
      {...cleanProps}
    />
  );
}

export function SidebarSeparator({
  className,
  ...props
}: React.ComponentProps<typeof Separator>) {
  const { _fgT, _fgt, _fgS, _fgs, _fgB, _fgb, ...cleanProps } = props as any;
  return (
    <Separator
      data-slot="sidebar-separator"
      data-sidebar="separator"
      className={cn("bg-sidebar-border mx-2 w-auto", className)}
      {...cleanProps}
    />
  );
}

export function SidebarContent({ className, ...props }: React.ComponentProps<"div">) {
  const { _fgT, _fgt, _fgS, _fgs, _fgB, _fgb, ...cleanProps } = props as any;
  return (
    <div
      data-slot="sidebar-content"
      data-sidebar="content"
      className={cn(
        "flex min-h-0 flex-1 flex-col gap-2 overflow-y-auto overflow-x-hidden [scrollbar-gutter:stable] group-data-[collapsible=icon]:overflow-hidden",
        className,
      )}
      {...cleanProps}
    />
  );
}

export function SidebarGroup({ className, ...props }: React.ComponentProps<"div">) {
  const { _fgT, _fgt, _fgS, _fgs, _fgB, _fgb, ...cleanProps } = props as any;
  return (
    <div
      data-slot="sidebar-group"
      data-sidebar="group"
      className={cn("relative flex w-full min-w-0 flex-col p-2", className)}
      {...cleanProps}
    />
  );
}

export function SidebarGroupLabel({
  className,
  asChild = false,
  ...props
}: React.ComponentProps<"div"> & { asChild?: boolean }) {
  if (asChild && !hasRadixSlot) {
    warnMissingSlot();
  }
  const Comp = asChild && hasRadixSlot ? Slot : "div";
  const { _fgT, _fgt, _fgS, _fgs, _fgB, _fgb, ...cleanProps } = props as any;

  return (
    <Comp
      data-slot="sidebar-group-label"
      data-sidebar="group-label"
      className={cn(
        "text-sidebar-foreground/70 ring-sidebar-ring flex h-8 shrink-0 items-center rounded-none px-2 text-xs font-medium outline-hidden transition-[margin,opacity] duration-200 ease-linear focus-visible:ring-2 [&>svg]:size-4 [&>svg]:shrink-0 uppercase tracking-wide",
        "group-data-[collapsible=icon]:-mt-8 group-data-[collapsible=icon]:opacity-0",
        className,
      )}
      {...cleanProps}
    />
  );
}

export function SidebarGroupAction({
  className,
  asChild = false,
  ...props
}: React.ComponentProps<"button"> & { asChild?: boolean }) {
  if (asChild && !hasRadixSlot) {
    warnMissingSlot();
  }
  const Comp = asChild && hasRadixSlot ? Slot : "button";
  const { _fgT, _fgt, _fgS, _fgs, _fgB, _fgb, ...cleanProps } = props as any;

  return (
    <Comp
      data-slot="sidebar-group-action"
      data-sidebar="group-action"
      className={cn(
        "text-sidebar-foreground ring-sidebar-ring hover:bg-sidebar-accent hover:text-sidebar-accent-foreground absolute top-3.5 right-3 flex aspect-square w-5 items-center justify-center rounded-none p-0 outline-hidden transition-transform focus-visible:ring-2 [&>svg]:size-4 [&>svg]:shrink-0",
        "after:absolute after:-inset-2 md:after:hidden",
        "group-data-[collapsible=icon]:hidden",
        className,
      )}
      {...cleanProps}
    />
  );
}

export function SidebarGroupContent({
  className,
  ...props
}: React.ComponentProps<"div">) {
  const { _fgT, _fgt, _fgS, _fgs, _fgB, _fgb, ...cleanProps } = props as any;
  return (
    <div
      data-slot="sidebar-group-content"
      data-sidebar="group-content"
      className={cn("w-full text-sm", className)}
      {...cleanProps}
    />
  );
}

export function SidebarMenu({ className, ...props }: React.ComponentProps<"ul">) {
  const { _fgT, _fgt, _fgS, _fgs, _fgB, _fgb, ...cleanProps } = props as any;
  return (
    <ul
      data-slot="sidebar-menu"
      data-sidebar="menu"
      className={cn("flex w-full min-w-0 flex-col gap-1", className)}
      {...cleanProps}
    />
  );
}

export function SidebarMenuItem({ className, ...props }: React.ComponentProps<"li">) {
  const { _fgT, _fgt, _fgS, _fgs, _fgB, _fgb, ...cleanProps } = props as any;
  return (
    <li
      data-slot="sidebar-menu-item"
      data-sidebar="menu-item"
      className={cn("group/menu-item relative", className)}
      {...cleanProps}
    />
  );
}

const sidebarMenuButtonVariants = cva(
  "peer/menu-button flex w-full items-center gap-2 overflow-hidden rounded-none p-2 text-left text-sm outline-hidden ring-sidebar-ring transition-[width,height,padding] hover:bg-sidebar-accent hover:text-sidebar-accent-foreground focus-visible:ring-2 focus-visible:ring-inset data-[active=true]:focus-visible:ring-0 active:bg-sidebar-accent active:text-sidebar-accent-foreground disabled:pointer-events-none disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:opacity-50 data-[active=true]:bg-sidebar-accent data-[active=true]:font-medium data-[active=true]:text-sidebar-accent-foreground data-[state=open]:hover:bg-sidebar-accent data-[state=open]:hover:text-sidebar-accent-foreground group-data-[collapsible=icon]:size-8! group-data-[collapsible=icon]:p-2! [&>span:last-child]:truncate [&>svg]:size-4 [&>svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
        outline:
          "bg-background shadow-[0_0_0_1px_hsl(var(--sidebar-border))] hover:bg-sidebar-accent hover:text-sidebar-accent-foreground hover:shadow-[0_0_0_1px_hsl(var(--sidebar-accent))]",
      },
      size: {
        default: "h-8 text-sm",
        sm: "h-7 text-xs",
        lg: "h-12 text-sm group-data-[collapsible=icon]:p-0!",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export function SidebarMenuButton({
  asChild = false,
  isActive = false,
  variant = "default",
  size = "default",
  tooltip,
  className,
  ...props
}: React.ComponentProps<"button"> & {
  asChild?: boolean;
  isActive?: boolean;
  tooltip?: string | React.ComponentProps<typeof TooltipContent>;
} & VariantProps<typeof sidebarMenuButtonVariants>) {
  if (asChild && !hasRadixSlot) {
    warnMissingSlot();
  }
  const Comp = asChild && hasRadixSlot ? Slot : "button";
  const { isMobile, state } = useSidebar();
  const { _fgT, _fgt, _fgS, _fgs, _fgB, _fgb, ...cleanProps } = props as any;

  const button = (
    <Comp
      data-slot="sidebar-menu-button"
      data-sidebar="menu-button"
      data-size={size}
      data-active={isActive}
      className={cn(sidebarMenuButtonVariants({ variant, size }), className)}
      {...cleanProps}
    />
  );

  if (!tooltip) {
    return button;
  }

  if (typeof tooltip === "string") {
    tooltip = {
      children: tooltip,
    };
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>{button}</TooltipTrigger>
      <TooltipContent
        side="right"
        align="center"
        hidden={state !== "collapsed" || isMobile}
        {...tooltip}
      />
    </Tooltip>
  );
}

export function SidebarMenuAction({
  className,
  asChild = false,
  showOnHover = false,
  ...props
}: React.ComponentProps<"button"> & {
  asChild?: boolean;
  showOnHover?: boolean;
}) {
  if (asChild && !hasRadixSlot) {
    warnMissingSlot();
  }
  const Comp = asChild && hasRadixSlot ? Slot : "button";
  const { _fgT, _fgt, _fgS, _fgs, _fgB, _fgb, ...cleanProps } = props as any;

  // Give the sibling SidebarMenuButton pr-8 to clear this action. The stock
  // group-has-[menu-action] padding made Chrome restyle every page on each
  // DOM insertion, so it was removed.
  return (
    <Comp
      data-slot="sidebar-menu-action"
      data-sidebar="menu-action"
      className={cn(
        "text-sidebar-foreground ring-sidebar-ring hover:bg-sidebar-accent hover:text-sidebar-accent-foreground peer-hover/menu-button:text-sidebar-accent-foreground absolute top-1.5 right-1 flex aspect-square w-5 items-center justify-center rounded-none p-0 outline-hidden transition-transform focus-visible:ring-2 [&>svg]:size-4 [&>svg]:shrink-0",
        "after:absolute after:-inset-2 md:after:hidden",
        "peer-data-[size=sm]/menu-button:top-1",
        "peer-data-[size=default]/menu-button:top-1.5",
        "peer-data-[size=lg]/menu-button:top-2.5",
        "group-data-[collapsible=icon]:hidden",
        showOnHover &&
          "peer-data-[active=true]/menu-button:text-sidebar-accent-foreground group-focus-within/menu-item:opacity-100 group-hover/menu-item:opacity-100 data-[state=open]:opacity-100 md:opacity-0",
        className,
      )}
      {...cleanProps}
    />
  );
}

export function SidebarMenuBadge({
  className,
  ...props
}: React.ComponentProps<"div">) {
  const { _fgT, _fgt, _fgS, _fgs, _fgB, _fgb, ...cleanProps } = props as any;
  return (
    <div
      data-slot="sidebar-menu-badge"
      data-sidebar="menu-badge"
      className={cn(
        "text-sidebar-foreground pointer-events-none absolute right-1 flex h-5 min-w-5 items-center justify-center rounded-none px-1 text-xs font-medium tabular-nums select-none",
        "peer-hover/menu-button:text-sidebar-accent-foreground peer-data-[active=true]/menu-button:text-sidebar-accent-foreground",
        "peer-data-[size=sm]/menu-button:top-1",
        "peer-data-[size=default]/menu-button:top-1.5",
        "peer-data-[size=lg]/menu-button:top-2.5",
        "group-data-[collapsible=icon]:hidden",
        className,
      )}
      {...cleanProps}
    />
  );
}

export function SidebarMenuSkeleton({
  className,
  showIcon = false,
  ...props
}: React.ComponentProps<"div"> & {
  showIcon?: boolean;
}) {
  const width = React.useMemo(() => {
    return `${Math.floor(Math.random() * 40) + 50}%`;
  }, []);

  const { _fgT, _fgt, _fgS, _fgs, _fgB, _fgb, ...cleanProps } = props as any;

  return (
    <div
      data-slot="sidebar-menu-skeleton"
      data-sidebar="menu-skeleton"
      className={cn("flex h-8 items-center gap-2 rounded-md px-2", className)}
      {...cleanProps}
    >
      {showIcon && (
        <Skeleton
          className="size-4 rounded-md"
          data-sidebar="menu-skeleton-icon"
        />
      )}
      <Skeleton
        className="h-4 max-w-(--skeleton-width) flex-1"
        data-sidebar="menu-skeleton-text"
        style={
          {
            "--skeleton-width": width,
          } as React.CSSProperties
        }
      />
    </div>
  );
}

export function SidebarMenuSub({ className, ...props }: React.ComponentProps<"ul">) {
  const { _fgT, _fgt, _fgS, _fgs, _fgB, _fgb, ...cleanProps } = props as any;
  return (
    <ul
      data-slot="sidebar-menu-sub"
      data-sidebar="menu-sub"
      className={cn(
        "mx-3.5 flex min-w-0 translate-x-px flex-col gap-1 px-2.5 py-0.5",
        "group-data-[collapsible=icon]:hidden",
        className,
      )}
      {...cleanProps}
    />
  );
}

export function SidebarMenuSubItem({
  className,
  ...props
}: React.ComponentProps<"li">) {
  const { _fgT, _fgt, _fgS, _fgs, _fgB, _fgb, ...cleanProps } = props as any;
  return (
    <li
      data-slot="sidebar-menu-sub-item"
      data-sidebar="menu-sub-item"
      className={cn("group/menu-sub-item relative", className)}
      {...cleanProps}
    />
  );
}

export function SidebarMenuSubButton({
  asChild = false,
  size = "md",
  isActive = false,
  className,
  ...props
}: React.ComponentProps<"a"> & {
  asChild?: boolean;
  size?: "sm" | "md";
  isActive?: boolean;
}) {
  if (asChild && !hasRadixSlot) {
    warnMissingSlot();
  }
  const Comp = asChild && hasRadixSlot ? Slot : "a";
  const { _fgT, _fgt, _fgS, _fgs, _fgB, _fgb, ...cleanProps } = props as any;

  return (
    <Comp
      data-slot="sidebar-menu-sub-button"
      data-sidebar="menu-sub-button"
      data-size={size}
      data-active={isActive}
      className={cn(
        "text-sidebar-foreground ring-sidebar-ring hover:bg-sidebar-accent hover:text-sidebar-accent-foreground active:bg-sidebar-accent active:text-sidebar-accent-foreground [&>svg]:text-sidebar-accent-foreground flex h-7 min-w-0 -translate-x-px items-center gap-2 overflow-hidden rounded-none px-2 outline-hidden focus-visible:ring-2 focus-visible:ring-inset disabled:pointer-events-none disabled:opacity-50 aria-disabled:pointer-events-none aria-disabled:opacity-50 [&>span:last-child]:truncate [&>svg]:size-4 [&>svg]:shrink-0",
        "data-[active=true]:bg-sidebar-accent data-[active=true]:text-sidebar-accent-foreground",
        size === "sm" && "text-xs",
        size === "md" && "text-sm",
        "group-data-[collapsible=icon]:hidden",
        className,
      )}
      {...cleanProps}
    />
  );
}
